import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

// After Google auth, Supabase redirects back here with the outcome. On
// success supabase-js picks the session up from the URL automatically; on
// failure (e.g. new sign-ups disabled) it instead appends error/error_description
// as query or hash params, which supabase-js does NOT surface on its own —
// so without this the user just lands back on a blank login page.
function readOAuthError() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const description = params.get('error_description') || hashParams.get('error_description');
  const code = params.get('error') || hashParams.get('error');
  return description || code ? decodeURIComponent(description || code).replace(/\+/g, ' ') : null;
}

const Login = () => {
  const { session, signInWithGoogle } = useAuth();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  useEffect(() => {
    const oauthError = readOAuthError();
    if (oauthError) {
      setError(oauthError);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  if (session) {
    const redirectTo = location.state?.from?.pathname || '/';
    return <Navigate to={redirectTo} replace />;
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleSubmitting(true);
    setError(null);

    const { error: oauthError } = await signInWithGoogle();

    if (oauthError) {
      setError(oauthError.message);
      setIsGoogleSubmitting(false);
    }
    // On success the browser is redirected to Google, so there's nothing
    // further to do here.
  };

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Welcome back</h2>
        <p className="login-subtitle">Sign in with your Crossroads Co.Lab account to continue.</p>
        <button
          type="button"
          className="google-sign-in-button"
          onClick={handleGoogleSignIn}
          disabled={isGoogleSubmitting}
        >
          <svg className="google-icon" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          <span>{isGoogleSubmitting ? 'Redirecting…' : 'Sign in with Google'}</span>
        </button>
        {error && <div className="login-error">{error}</div>}
      </div>
    </div>
  );
};

export default Login;
